import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../lib/api';
import { StatusBadge, PaymentBadge, RouteBadge, InvoiceStatusBadge, AgentStatusBadge } from '../components/StatusBadge';
import {
  Anchor, LayoutDashboard, Ship, Calendar, Users, FileText, CreditCard,
  LogOut, Plus, Edit, Trash2, Check, X, Eye, Download, DollarSign, Filter,
  UserCheck, UserX, Key, Ban, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState(null);
  const [agentStats, setAgentStats] = useState(null);
  const [cruises, setCruises] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [creditReport, setCreditReport] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [bookingFilter, setBookingFilter] = useState('');
  const [bookingDateFrom, setBookingDateFrom] = useState(null);
  const [bookingDateTo, setBookingDateTo] = useState(null);
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState(null);
  const [invoiceDateTo, setInvoiceDateTo] = useState(null);
  const [invoiceAgentFilter, setInvoiceAgentFilter] = useState('');
  const [agentStatusFilter, setAgentStatusFilter] = useState('');

  // Modals
  const [cruiseModal, setCruiseModal] = useState(false);
  const [bookingModal, setBookingModal] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [createInvoiceModal, setCreateInvoiceModal] = useState(false);
  const [creditDetailModal, setCreditDetailModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [editingCruise, setEditingCruise] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedAgentCredit, setSelectedAgentCredit] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Suspend form
  const [suspendReason, setSuspendReason] = useState('');
  
  // Password form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Cruise form
  const [cruiseForm, setCruiseForm] = useState({
    name: '',
    description: '',
    active: true,
    image_url: '',
    cabin_types: [],
    suite_types: [],
    schedules: [],
  });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: '',
  });

  // Create invoice form
  const [createInvoiceForm, setCreateInvoiceForm] = useState({
    booking_id: '',
    tax_rate: 0,
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeSection === 'bookings') {
      loadBookings();
    } else if (activeSection === 'invoices') {
      loadInvoices();
    } else if (activeSection === 'credit') {
      loadCreditReport();
    } else if (activeSection === 'agents') {
      loadAgents();
    }
  }, [activeSection, bookingFilter, bookingDateFrom, bookingDateTo, invoiceFilter, invoiceDateFrom, invoiceDateTo, invoiceAgentFilter, agentStatusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, cruisesRes, bookingsRes, agentsRes, agentStatsRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getCruises(),
        adminApi.getBookings({}),
        adminApi.getAgents({}),
        adminApi.getAgentStats(),
      ]);
      setStats(statsRes.data);
      setCruises(cruisesRes.data);
      setBookings(bookingsRes.data);
      setAgents(agentsRes.data);
      setAgentStats(agentStatsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const params = {};
      if (agentStatusFilter && agentStatusFilter !== 'all') params.status = agentStatusFilter;
      
      const [agentsRes, agentStatsRes] = await Promise.all([
        adminApi.getAgents(params),
        adminApi.getAgentStats(),
      ]);
      setAgents(agentsRes.data);
      setAgentStats(agentStatsRes.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const params = {};
      if (bookingFilter && bookingFilter !== 'all') params.status = bookingFilter;
      if (bookingDateFrom) params.date_from = format(bookingDateFrom, 'yyyy-MM-dd');
      if (bookingDateTo) params.date_to = format(bookingDateTo, 'yyyy-MM-dd');
      
      const res = await adminApi.getBookings(params);
      setBookings(res.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const params = {};
      if (invoiceFilter && invoiceFilter !== 'all') params.status = invoiceFilter;
      if (invoiceDateFrom) params.date_from = format(invoiceDateFrom, 'yyyy-MM-dd');
      if (invoiceDateTo) params.date_to = format(invoiceDateTo, 'yyyy-MM-dd');
      if (invoiceAgentFilter && invoiceAgentFilter !== 'all') params.agent_id = invoiceAgentFilter;
      
      const res = await adminApi.getInvoices(params);
      setInvoices(res.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const loadCreditReport = async () => {
    try {
      const res = await adminApi.getCreditReport();
      setCreditReport(res.data);
    } catch (error) {
      console.error('Error loading credit report:', error);
    }
  };

  // Agent Management Functions
  const handleApproveAgent = async (agentId) => {
    try {
      await adminApi.approveAgent(agentId);
      toast.success('Agent approved successfully');
      loadAgents();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve agent');
    }
  };

  const openSuspendModal = (agent) => {
    setSelectedAgent(agent);
    setSuspendReason('');
    setSuspendModal(true);
  };

  const handleSuspendAgent = async () => {
    if (!suspendReason.trim()) {
      toast.error('Please provide a reason for suspension');
      return;
    }
    try {
      await adminApi.suspendAgent(selectedAgent.id, suspendReason);
      toast.success('Agent suspended');
      setSuspendModal(false);
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend agent');
    }
  };

  const handleReactivateAgent = async (agentId) => {
    try {
      await adminApi.reactivateAgent(agentId);
      toast.success('Agent reactivated');
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate agent');
    }
  };

  const openPasswordModal = (agent) => {
    setSelectedAgent(agent);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordModal(true);
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await adminApi.changeAgentPassword(selectedAgent.id, newPassword);
      toast.success('Password changed successfully');
      setPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const openCruiseModal = (cruise = null) => {
    if (cruise) {
      setEditingCruise(cruise);
      setCruiseForm({
        name: cruise.name,
        description: cruise.description,
        active: cruise.active,
        image_url: cruise.image_url || '',
        cabin_types: cruise.cabin_types || [],
        suite_types: cruise.suite_types || [],
        schedules: cruise.schedules || [],
      });
    } else {
      setEditingCruise(null);
      setCruiseForm({
        name: '',
        description: '',
        active: true,
        image_url: '',
        cabin_types: [],
        suite_types: [],
        schedules: [],
      });
    }
    setCruiseModal(true);
  };

  const handleSaveCruise = async () => {
    try {
      if (editingCruise) {
        await adminApi.updateCruise(editingCruise.id, cruiseForm);
        toast.success('Cruise updated');
      } else {
        await adminApi.createCruise(cruiseForm);
        toast.success('Cruise created');
      }
      setCruiseModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cruise');
    }
  };

  const handleDeleteCruise = async (cruiseId) => {
    if (!window.confirm('Are you sure you want to delete this cruise?')) return;
    try {
      await adminApi.deleteCruise(cruiseId);
      toast.success('Cruise deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete cruise');
    }
  };

  const openBookingModal = (booking) => {
    setSelectedBooking(booking);
    setBookingModal(true);
  };

  const handleBookingAction = async (action, paymentType = null) => {
    try {
      const updateData = { status: action };
      if (paymentType) {
        updateData.payment_type = paymentType;
      }
      await adminApi.updateBooking(selectedBooking.id, updateData);
      toast.success(`Booking ${action}`);
      setBookingModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const openInvoiceModal = async (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceModal(true);
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await adminApi.downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount: invoice.balance_due.toString(),
      payment_method: 'bank_transfer',
      reference_number: '',
      notes: '',
    });
    setPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    try {
      await adminApi.recordPayment(selectedInvoice.id, {
        invoice_id: selectedInvoice.id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        notes: paymentForm.notes || null,
      });
      toast.success('Payment recorded');
      setPaymentModal(false);
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openCreateInvoiceModal = (booking) => {
    setCreateInvoiceForm({
      booking_id: booking.id,
      tax_rate: 0,
      due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      notes: '',
    });
    setSelectedBooking(booking);
    setCreateInvoiceModal(true);
  };

  const handleCreateInvoice = async () => {
    try {
      await adminApi.createInvoice({
        booking_id: createInvoiceForm.booking_id,
        tax_rate: parseFloat(createInvoiceForm.tax_rate) / 100,
        due_date: createInvoiceForm.due_date,
        notes: createInvoiceForm.notes || null,
      });
      toast.success('Invoice created');
      setCreateInvoiceModal(false);
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const openCreditDetail = async (agentId) => {
    try {
      const res = await adminApi.getAgentCreditDetail(agentId);
      setSelectedAgentCredit(res.data);
      setCreditDetailModal(true);
    } catch (error) {
      toast.error('Failed to load agent credit details');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const addCabinType = () => {
    setCruiseForm({
      ...cruiseForm,
      cabin_types: [
        ...cruiseForm.cabin_types,
        { id: crypto.randomUUID(), name: '', total_quantity: 10, max_occupancy: 2, price_per_night: 150 }
      ]
    });
  };

  const addSuiteType = () => {
    setCruiseForm({
      ...cruiseForm,
      suite_types: [
        ...cruiseForm.suite_types,
        { id: crypto.randomUUID(), name: '', total_quantity: 4, max_occupancy: 4, price_per_night: 500 }
      ]
    });
  };

  const addSchedule = () => {
    setCruiseForm({
      ...cruiseForm,
      schedules: [
        ...cruiseForm.schedules,
        { id: crypto.randomUUID(), route: 'luxor_to_aswan', duration_nights: 4, departure_day: 'thursday', active: true }
      ]
    });
  };

  const updateCabinType = (index, field, value) => {
    const updated = [...cruiseForm.cabin_types];
    updated[index][field] = field === 'name' ? value : Number(value);
    setCruiseForm({ ...cruiseForm, cabin_types: updated });
  };

  const updateSuiteType = (index, field, value) => {
    const updated = [...cruiseForm.suite_types];
    updated[index][field] = field === 'name' ? value : Number(value);
    setCruiseForm({ ...cruiseForm, suite_types: updated });
  };

  const updateSchedule = (index, field, value) => {
    const updated = [...cruiseForm.schedules];
    updated[index][field] = field === 'duration_nights' ? Number(value) : value;
    setCruiseForm({ ...cruiseForm, schedules: updated });
  };

  const removeCabinType = (index) => {
    setCruiseForm({
      ...cruiseForm,
      cabin_types: cruiseForm.cabin_types.filter((_, i) => i !== index)
    });
  };

  const removeSuiteType = (index) => {
    setCruiseForm({
      ...cruiseForm,
      suite_types: cruiseForm.suite_types.filter((_, i) => i !== index)
    });
  };

  const removeSchedule = (index) => {
    setCruiseForm({
      ...cruiseForm,
      schedules: cruiseForm.schedules.filter((_, i) => i !== index)
    });
  };

  const clearBookingFilters = () => {
    setBookingFilter('');
    setBookingDateFrom(null);
    setBookingDateTo(null);
  };

  const clearInvoiceFilters = () => {
    setInvoiceFilter('');
    setInvoiceDateFrom(null);
    setInvoiceDateTo(null);
    setInvoiceAgentFilter('');
  };

  const sidebarItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'cruises', icon: Ship, label: 'Cruises' },
    { id: 'bookings', icon: FileText, label: 'Bookings' },
    { id: 'invoices', icon: DollarSign, label: 'Invoices' },
    { id: 'credit', icon: CreditCard, label: 'Credit Report' },
    { id: 'agents', icon: Users, label: 'Agents' },
  ];

  const routeLabels = {
    luxor_to_aswan: 'Luxor → Aswan',
    aswan_to_luxor: 'Aswan → Luxor',
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#0F172A] rounded-sm flex items-center justify-center">
            <Anchor className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <span className="logo-text text-lg block">Nile Cruise</span>
            <span className="text-xs text-slate-500">Admin Portal</span>
          </div>
        </div>

        <nav className="space-y-1 flex-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`sidebar-link w-full ${activeSection === item.id ? 'active' : ''}`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="border-t border-slate-200 pt-4 mt-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">Administrator</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-500"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main" data-testid="admin-dashboard">
        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-3xl font-bold text-[#0F172A] mb-8">Dashboard Overview</h1>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="stat-card">
                <div className="stat-value">{stats?.active_cruises || 0}</div>
                <div className="stat-label">Active Cruises</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats?.total_agents || 0}</div>
                <div className="stat-label">Registered Agents</div>
              </div>
              <div className="stat-card">
                <div className="stat-value text-yellow-600">{stats?.pending_bookings || 0}</div>
                <div className="stat-label">Pending Bookings</div>
              </div>
              <div className="stat-card">
                <div className="stat-value text-green-600">${stats?.total_revenue?.toLocaleString() || 0}</div>
                <div className="stat-label">Total Revenue</div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-lg mb-4">Booking Statistics</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total Bookings</span>
                    <span className="font-bold">{stats?.total_bookings || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Confirmed</span>
                    <span className="font-bold text-green-600">{stats?.confirmed_bookings || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Pending</span>
                    <span className="font-bold text-yellow-600">{stats?.pending_bookings || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Rejected</span>
                    <span className="font-bold text-red-600">{stats?.rejected_bookings || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-lg mb-4">Recent Activity</h3>
                {bookings.slice(0, 5).map((booking) => (
                  <div key={booking.id} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-sm">{booking.booking_number}</p>
                      <p className="text-xs text-slate-500">{booking.cruise_name}</p>
                    </div>
                    <StatusBadge status={booking.status} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Cruises Section */}
        {activeSection === 'cruises' && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Cruise Management</h1>
              <Button className="btn-primary" onClick={() => openCruiseModal()} data-testid="add-cruise-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Cruise
              </Button>
            </div>

            <div className="grid gap-6">
              {cruises.map((cruise) => (
                <div key={cruise.id} className="bg-white rounded-sm border border-slate-200 p-6" data-testid={`cruise-card-${cruise.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex gap-4">
                      <div 
                        className="w-32 h-24 rounded-sm bg-cover bg-center"
                        style={{ backgroundImage: `url(${cruise.image_url || 'https://images.unsplash.com/photo-1713144453052-0cc2518faa4a?w=200'})` }}
                      />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading text-xl font-bold">{cruise.name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${cruise.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {cruise.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-slate-500 text-sm mb-2">{cruise.description}</p>
                        <div className="flex gap-4 text-sm text-slate-600">
                          <span>{cruise.cabin_types?.length || 0} cabin types</span>
                          <span>{cruise.suite_types?.length || 0} suite types</span>
                          <span>{cruise.schedules?.length || 0} schedules</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openCruiseModal(cruise)} data-testid={`edit-cruise-${cruise.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteCruise(cruise.id)} data-testid={`delete-cruise-${cruise.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bookings Section */}
        {activeSection === 'bookings' && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Booking Management</h1>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm">Filters</span>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <Select value={bookingFilter} onValueChange={setBookingFilter}>
                  <SelectTrigger data-testid="booking-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {bookingDateFrom ? format(bookingDateFrom, 'MMM d, yyyy') : 'From Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={bookingDateFrom}
                      onSelect={setBookingDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {bookingDateTo ? format(bookingDateTo, 'MMM d, yyyy') : 'To Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={bookingDateTo}
                      onSelect={setBookingDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button variant="ghost" onClick={clearBookingFilters} className="text-slate-500">
                  Clear Filters
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table-nile">
                  <thead>
                    <tr>
                      <th>Booking #</th>
                      <th>Agent</th>
                      <th>Cruise</th>
                      <th>Route</th>
                      <th>Departure</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} data-testid={`admin-booking-row-${booking.id}`}>
                        <td className="font-mono text-sm">{booking.booking_number}</td>
                        <td>{booking.agent_name}</td>
                        <td className="font-medium">{booking.cruise_name}</td>
                        <td><RouteBadge route={booking.route} /></td>
                        <td>{booking.departure_date}</td>
                        <td className="font-bold">${booking.total_amount.toLocaleString()}</td>
                        <td><StatusBadge status={booking.status} /></td>
                        <td><PaymentBadge type={booking.payment_type} /></td>
                        <td>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openBookingModal(booking)}
                              data-testid={`view-booking-${booking.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {booking.status === 'confirmed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-[#D4AF37]"
                                onClick={() => openCreateInvoiceModal(booking)}
                                data-testid={`create-invoice-${booking.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Section */}
        {activeSection === 'invoices' && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h1 className="font-heading text-3xl font-bold text-[#0F172A]">Invoice Management</h1>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm">Filters</span>
              </div>
              <div className="grid md:grid-cols-5 gap-4">
                <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
                  <SelectTrigger data-testid="invoice-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="issued">Issued</SelectItem>
                    <SelectItem value="partially_paid">Partially Paid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={invoiceAgentFilter} onValueChange={setInvoiceAgentFilter}>
                  <SelectTrigger data-testid="invoice-agent-filter">
                    <SelectValue placeholder="All Agents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Agents</SelectItem>
                    {agents.map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {invoiceDateFrom ? format(invoiceDateFrom, 'MMM d, yyyy') : 'Due From'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={invoiceDateFrom}
                      onSelect={setInvoiceDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <Calendar className="mr-2 h-4 w-4" />
                      {invoiceDateTo ? format(invoiceDateTo, 'MMM d, yyyy') : 'Due To'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={invoiceDateTo}
                      onSelect={setInvoiceDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Button variant="ghost" onClick={clearInvoiceFilters} className="text-slate-500">
                  Clear Filters
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table-nile">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Agent</th>
                      <th>Booking</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <td className="font-mono text-sm">{invoice.invoice_number}</td>
                        <td>{invoice.agent_name}</td>
                        <td className="font-mono text-sm">{invoice.booking_number}</td>
                        <td className="font-bold">${invoice.total_amount.toLocaleString()}</td>
                        <td className="text-green-600">${invoice.amount_paid.toLocaleString()}</td>
                        <td className={invoice.balance_due > 0 ? 'text-amber-600 font-bold' : 'text-green-600'}>
                          ${invoice.balance_due.toLocaleString()}
                        </td>
                        <td>{invoice.due_date}</td>
                        <td><InvoiceStatusBadge status={invoice.status} /></td>
                        <td>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openInvoiceModal(invoice)}
                              data-testid={`view-invoice-${invoice.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPdf(invoice.id)}
                              data-testid={`download-invoice-${invoice.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600"
                                onClick={() => openPaymentModal(invoice)}
                                data-testid={`pay-invoice-${invoice.id}`}
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {invoices.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center py-8 text-slate-500">
                          No invoices found. Create invoices from confirmed bookings.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Credit Report Section */}
        {activeSection === 'credit' && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-3xl font-bold text-[#0F172A] mb-8">Credit Report</h1>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="stat-card">
                <div className="stat-value text-[#1D4ED8]">
                  ${creditReport.reduce((sum, r) => sum + r.total_invoiced, 0).toLocaleString()}
                </div>
                <div className="stat-label">Total Invoiced</div>
              </div>
              <div className="stat-card">
                <div className="stat-value text-green-600">
                  ${creditReport.reduce((sum, r) => sum + r.total_paid, 0).toLocaleString()}
                </div>
                <div className="stat-label">Total Collected</div>
              </div>
              <div className="stat-card">
                <div className="stat-value text-amber-600">
                  ${creditReport.reduce((sum, r) => sum + r.total_credit, 0).toLocaleString()}
                </div>
                <div className="stat-label">Outstanding Credit</div>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table-nile">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Company</th>
                      <th>Total Invoiced</th>
                      <th>Total Paid</th>
                      <th>Credit Balance</th>
                      <th>Pending</th>
                      <th>Overdue</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditReport.map((report) => (
                      <tr key={report.agent_id} data-testid={`credit-row-${report.agent_id}`}>
                        <td className="font-medium">{report.agent_name}</td>
                        <td>{report.agent_company || '-'}</td>
                        <td>${report.total_invoiced.toLocaleString()}</td>
                        <td className="text-green-600">${report.total_paid.toLocaleString()}</td>
                        <td className={report.total_credit > 0 ? 'text-amber-600 font-bold' : 'text-green-600'}>
                          ${report.total_credit.toLocaleString()}
                        </td>
                        <td>{report.pending_invoices}</td>
                        <td className={report.overdue_invoices > 0 ? 'text-red-600 font-bold' : ''}>
                          {report.overdue_invoices}
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCreditDetail(report.agent_id)}
                            data-testid={`view-credit-${report.agent_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Agents Section */}
        {activeSection === 'agents' && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-3xl font-bold text-[#0F172A] mb-6">Agent Management</h1>

            {/* Agent Stats */}
            {agentStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-sm border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase">Total Agents</p>
                  <p className="text-2xl font-bold">{agentStats.total_agents}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-sm border border-amber-200">
                  <p className="text-xs text-amber-600 uppercase">Pending Approval</p>
                  <p className="text-2xl font-bold text-amber-700">{agentStats.pending_agents}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-sm border border-green-200">
                  <p className="text-xs text-green-600 uppercase">Active</p>
                  <p className="text-2xl font-bold text-green-700">{agentStats.active_agents}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-sm border border-red-200">
                  <p className="text-xs text-red-600 uppercase">Suspended</p>
                  <p className="text-2xl font-bold text-red-700">{agentStats.suspended_agents}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="font-medium text-sm">Filter by Status</span>
              </div>
              <div className="flex gap-4">
                <Select value={agentStatusFilter} onValueChange={setAgentStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="agent-status-filter">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending Approval</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" onClick={() => setAgentStatusFilter('')} className="text-slate-500">
                  Clear Filter
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="table-nile">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Company</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Approved By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => (
                      <tr key={agent.id} data-testid={`agent-row-${agent.id}`}>
                        <td className="font-medium">{agent.name}</td>
                        <td>{agent.email}</td>
                        <td>{agent.company_name || '-'}</td>
                        <td>{agent.phone || '-'}</td>
                        <td>
                          <AgentStatusBadge status={agent.status || 'pending'} />
                          {agent.suspension_reason && (
                            <p className="text-xs text-red-500 mt-1">Reason: {agent.suspension_reason}</p>
                          )}
                        </td>
                        <td className="text-sm">
                          {agent.approved_by ? (
                            <span className="text-slate-600">{agent.approved_by}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            {agent.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApproveAgent(agent.id)}
                                data-testid={`approve-agent-${agent.id}`}
                                title="Approve Agent"
                              >
                                <UserCheck className="w-4 h-4" />
                              </Button>
                            )}
                            {agent.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => openSuspendModal(agent)}
                                data-testid={`suspend-agent-${agent.id}`}
                                title="Suspend Agent"
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            {agent.status === 'suspended' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleReactivateAgent(agent.id)}
                                data-testid={`reactivate-agent-${agent.id}`}
                                title="Reactivate Agent"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => openPasswordModal(agent)}
                              data-testid={`change-password-${agent.id}`}
                              title="Change Password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {agents.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-slate-500">
                          No agents found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Cruise Modal */}
      <Dialog open={cruiseModal} onOpenChange={setCruiseModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingCruise ? 'Edit Cruise' : 'Add New Cruise'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="cabins">Cabins</TabsTrigger>
                <TabsTrigger value="suites">Suites</TabsTrigger>
                <TabsTrigger value="schedules">Schedules</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="space-y-2">
                  <Label>Cruise Name</Label>
                  <Input
                    value={cruiseForm.name}
                    onChange={(e) => setCruiseForm({ ...cruiseForm, name: e.target.value })}
                    placeholder="e.g., River Tosca"
                    data-testid="cruise-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={cruiseForm.description}
                    onChange={(e) => setCruiseForm({ ...cruiseForm, description: e.target.value })}
                    placeholder="Describe the cruise..."
                    rows={3}
                    data-testid="cruise-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    value={cruiseForm.image_url}
                    onChange={(e) => setCruiseForm({ ...cruiseForm, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="cruise-image-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={cruiseForm.active}
                    onCheckedChange={(checked) => setCruiseForm({ ...cruiseForm, active: checked })}
                    data-testid="cruise-active-switch"
                  />
                  <Label>Active</Label>
                </div>
              </TabsContent>

              <TabsContent value="cabins" className="space-y-4">
                {cruiseForm.cabin_types.map((cabin, index) => (
                  <div key={cabin.id} className="p-4 border border-slate-200 rounded-sm space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Cabin {index + 1}</span>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeCabinType(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input value={cabin.name} onChange={(e) => updateCabinType(index, 'name', e.target.value)} placeholder="Standard Cabin" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Total Quantity</Label>
                        <Input type="number" value={cabin.total_quantity} onChange={(e) => updateCabinType(index, 'total_quantity', e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Max Occupancy</Label>
                        <Input type="number" value={cabin.max_occupancy} onChange={(e) => updateCabinType(index, 'max_occupancy', e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Price per Night ($)</Label>
                        <Input type="number" value={cabin.price_per_night} onChange={(e) => updateCabinType(index, 'price_per_night', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addCabinType} className="w-full" data-testid="add-cabin-type-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Cabin Type
                </Button>
              </TabsContent>

              <TabsContent value="suites" className="space-y-4">
                {cruiseForm.suite_types.map((suite, index) => (
                  <div key={suite.id} className="p-4 border-2 border-[#D4AF37]/30 rounded-sm space-y-3 bg-[#F3E5AB]/5">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-[#D4AF37]">Suite {index + 1}</span>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeSuiteType(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input value={suite.name} onChange={(e) => updateSuiteType(index, 'name', e.target.value)} placeholder="Presidential Suite" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Total Quantity</Label>
                        <Input type="number" value={suite.total_quantity} onChange={(e) => updateSuiteType(index, 'total_quantity', e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Max Occupancy</Label>
                        <Input type="number" value={suite.max_occupancy} onChange={(e) => updateSuiteType(index, 'max_occupancy', e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Price per Night ($)</Label>
                        <Input type="number" value={suite.price_per_night} onChange={(e) => updateSuiteType(index, 'price_per_night', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addSuiteType} className="w-full btn-gold" data-testid="add-suite-type-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Suite Type
                </Button>
              </TabsContent>

              <TabsContent value="schedules" className="space-y-4">
                {cruiseForm.schedules.map((schedule, index) => (
                  <div key={schedule.id} className="p-4 border border-slate-200 rounded-sm space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Schedule {index + 1}</span>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeSchedule(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Route</Label>
                        <Select value={schedule.route} onValueChange={(value) => updateSchedule(index, 'route', value)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="luxor_to_aswan">Luxor → Aswan</SelectItem>
                            <SelectItem value="aswan_to_luxor">Aswan → Luxor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Duration (nights)</Label>
                        <Select value={String(schedule.duration_nights)} onValueChange={(value) => updateSchedule(index, 'duration_nights', value)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2 nights</SelectItem>
                            <SelectItem value="3">3 nights</SelectItem>
                            <SelectItem value="4">4 nights</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Departure Day</Label>
                        <Select value={schedule.departure_day} onValueChange={(value) => updateSchedule(index, 'departure_day', value)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => (
                              <SelectItem key={day} value={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addSchedule} className="w-full" data-testid="add-schedule-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Schedule
                </Button>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCruiseModal(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveCruise} data-testid="save-cruise-btn">
              {editingCruise ? 'Update Cruise' : 'Create Cruise'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Action Modal */}
      <Dialog open={bookingModal} onOpenChange={setBookingModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Booking Number</p>
                  <p className="font-mono font-medium">{selectedBooking.booking_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <StatusBadge status={selectedBooking.status} />
                </div>
                <div>
                  <p className="text-slate-500">Agent</p>
                  <p className="font-medium">{selectedBooking.agent_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Cruise</p>
                  <p className="font-medium">{selectedBooking.cruise_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Route</p>
                  <RouteBadge route={selectedBooking.route} />
                </div>
                <div>
                  <p className="text-slate-500">Departure</p>
                  <p className="font-medium">{selectedBooking.departure_date}</p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <p className="text-slate-500 text-sm mb-2">Items</p>
                {selectedBooking.items.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm py-1">
                    <span>{item.quantity}x {item.accommodation_name}</span>
                    <span className="font-medium">${item.total_price.toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span>${selectedBooking.total_amount.toLocaleString()}</span>
                </div>
              </div>

              {selectedBooking.status === 'pending' && (
                <div className="border-t border-slate-200 pt-4 space-y-3">
                  <p className="font-medium">Take Action</p>
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleBookingAction('confirmed', 'instant')} data-testid="confirm-instant-btn">
                      <Check className="w-4 h-4 mr-2" /> Confirm (Instant)
                    </Button>
                    <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => handleBookingAction('confirmed', 'credit')} data-testid="confirm-credit-btn">
                      <Check className="w-4 h-4 mr-2" /> Confirm (Credit)
                    </Button>
                  </div>
                  <Button variant="outline" className="w-full text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleBookingAction('rejected')} data-testid="reject-booking-btn">
                    <X className="w-4 h-4 mr-2" /> Reject Booking
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Modal */}
      <Dialog open={invoiceModal} onOpenChange={setInvoiceModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Invoice Details</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Invoice Number</p>
                  <p className="font-mono font-medium">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <InvoiceStatusBadge status={selectedInvoice.status} />
                </div>
                <div>
                  <p className="text-slate-500">Due Date</p>
                  <p className="font-medium">{selectedInvoice.due_date}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Agent</p>
                  <p className="font-medium">{selectedInvoice.agent_name}</p>
                  {selectedInvoice.agent_company && <p className="text-xs text-slate-400">{selectedInvoice.agent_company}</p>}
                </div>
                <div>
                  <p className="text-slate-500">Booking</p>
                  <p className="font-mono text-sm">{selectedInvoice.booking_number}</p>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="flex justify-between text-sm py-1">
                  <span>Subtotal</span>
                  <span>${selectedInvoice.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm py-1">
                  <span>Tax ({(selectedInvoice.tax_rate * 100).toFixed(1)}%)</span>
                  <span>${selectedInvoice.tax_amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold py-1 border-t">
                  <span>Total</span>
                  <span>${selectedInvoice.total_amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-green-600 py-1">
                  <span>Paid</span>
                  <span>${selectedInvoice.amount_paid.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-amber-600 py-1">
                  <span>Balance Due</span>
                  <span>${selectedInvoice.balance_due.toLocaleString()}</span>
                </div>
              </div>

              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-medium mb-2">Payment History</p>
                  {selectedInvoice.payments.map((payment, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-100">
                      <span>{payment.created_at?.slice(0, 10)} - {payment.payment_method.replace('_', ' ')}</span>
                      <span className="text-green-600">${payment.amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => handleDownloadPdf(selectedInvoice.id)}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                  <Button className="btn-gold" onClick={() => { setInvoiceModal(false); openPaymentModal(selectedInvoice); }}>
                    <DollarSign className="w-4 h-4 mr-2" /> Record Payment
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={paymentModal} onOpenChange={setPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Record Payment</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-sm text-sm">
                <p><strong>Invoice:</strong> {selectedInvoice.invoice_number}</p>
                <p><strong>Balance Due:</strong> <span className="text-amber-600 font-bold">${selectedInvoice.balance_due.toLocaleString()}</span></p>
              </div>

              <div className="space-y-2">
                <Label>Payment Amount ($)</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  max={selectedInvoice.balance_due}
                  data-testid="payment-amount-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Reference Number (Optional)</Label>
                <Input
                  value={paymentForm.reference_number}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                  placeholder="e.g., TRX-123456"
                  data-testid="payment-reference-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPaymentModal(false)}>Cancel</Button>
                <Button className="btn-gold" onClick={handleRecordPayment} data-testid="submit-payment-btn">
                  Record Payment
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal */}
      <Dialog open={createInvoiceModal} onOpenChange={setCreateInvoiceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Create Invoice</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-sm text-sm">
                <p><strong>Booking:</strong> {selectedBooking.booking_number}</p>
                <p><strong>Amount:</strong> ${selectedBooking.total_amount.toLocaleString()}</p>
                <p><strong>Agent:</strong> {selectedBooking.agent_name}</p>
              </div>

              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  value={createInvoiceForm.tax_rate}
                  onChange={(e) => setCreateInvoiceForm({ ...createInvoiceForm, tax_rate: e.target.value })}
                  placeholder="0"
                  data-testid="invoice-tax-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={createInvoiceForm.due_date}
                  onChange={(e) => setCreateInvoiceForm({ ...createInvoiceForm, due_date: e.target.value })}
                  data-testid="invoice-due-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={createInvoiceForm.notes}
                  onChange={(e) => setCreateInvoiceForm({ ...createInvoiceForm, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateInvoiceModal(false)}>Cancel</Button>
                <Button className="btn-primary" onClick={handleCreateInvoice} data-testid="create-invoice-btn">
                  Create Invoice
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credit Detail Modal */}
      <Dialog open={creditDetailModal} onOpenChange={setCreditDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Agent Credit Details</DialogTitle>
          </DialogHeader>
          
          {selectedAgentCredit && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-sm">
                  <h3 className="font-medium">{selectedAgentCredit.agent.name}</h3>
                  {selectedAgentCredit.agent.company_name && (
                    <p className="text-sm text-slate-500">{selectedAgentCredit.agent.company_name}</p>
                  )}
                  <p className="text-sm text-slate-500">{selectedAgentCredit.agent.email}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-sm">
                    <p className="text-xs text-blue-600 uppercase">Total Invoiced</p>
                    <p className="text-xl font-bold text-blue-700">${selectedAgentCredit.summary.total_invoiced.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-sm">
                    <p className="text-xs text-green-600 uppercase">Total Paid</p>
                    <p className="text-xl font-bold text-green-700">${selectedAgentCredit.summary.total_paid.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-sm">
                    <p className="text-xs text-amber-600 uppercase">Outstanding</p>
                    <p className="text-xl font-bold text-amber-700">${selectedAgentCredit.summary.total_credit.toLocaleString()}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Invoice History</h4>
                  <table className="table-nile text-sm">
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Due Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAgentCredit.invoices.map((inv) => (
                        <tr key={inv.id}>
                          <td className="font-mono">{inv.invoice_number}</td>
                          <td>${inv.total_amount.toLocaleString()}</td>
                          <td className="text-green-600">${inv.amount_paid.toLocaleString()}</td>
                          <td className={inv.balance_due > 0 ? 'text-amber-600' : ''}>${inv.balance_due.toLocaleString()}</td>
                          <td>{inv.due_date}</td>
                          <td><InvoiceStatusBadge status={inv.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspend Agent Modal */}
      <Dialog open={suspendModal} onOpenChange={setSuspendModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-red-600">Suspend Agent</DialogTitle>
          </DialogHeader>
          
          {selectedAgent && (
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-sm border border-red-200">
                <p className="text-sm text-red-800">
                  You are about to suspend <strong>{selectedAgent.name}</strong> ({selectedAgent.email}). 
                  They will not be able to login until reactivated.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Suspension Reason *</Label>
                <Textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter the reason for suspension..."
                  rows={3}
                  data-testid="suspend-reason-input"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSuspendModal(false)}>Cancel</Button>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white" 
                  onClick={handleSuspendAgent}
                  data-testid="confirm-suspend-btn"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend Agent
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={passwordModal} onOpenChange={setPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Change Agent Password</DialogTitle>
          </DialogHeader>
          
          {selectedAgent && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-sm">
                <p className="text-sm">
                  Changing password for <strong>{selectedAgent.name}</strong> ({selectedAgent.email})
                </p>
              </div>

              <div className="space-y-2">
                <Label>New Password *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPasswordModal(false)}>Cancel</Button>
                <Button 
                  className="btn-primary" 
                  onClick={handleChangePassword}
                  data-testid="confirm-password-change-btn"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
